import React, {useEffect, useState} from "react";
import {
    LoyaltyRewardDetails,
    LoyaltyRewardFormValues,
    loyaltyRewardFormValuesFormValidation,
} from "@/data/entities/loyaltyReward";
import {FormattedMessage, useIntl} from "react-intl";
import {Controller, useFormContext, UseFormSetValue} from "react-hook-form";
import {Button, Icon, InputLabel, TextField, toggleDescriptionLabel, ToggleGroup, useNotifications} from "peggirkit";
import {defaultFormGridClassname, FormGridFiller} from "@widgets/layout/formLayout/FormLayout";
import {twJoin} from "tailwind-merge";
import {animatePulse} from "@/utils/LoadingUtils";
import {getInputValidationProps} from "@/data/Validation";
import {formatDateTime} from "@/utils/DateUtils";
import {CheckIcon} from "@heroicons/react/16/solid";
import {useMutation} from "@tanstack/react-query";
import {notifyDanger, notifySuccess} from "@/utils/NotifyUtils";
import {editLoyaltyReward} from "@/routing/Router";
import {useNavigate} from "react-router-dom";
import {SetLoyaltyRewardProps} from "@pages/authenticated/admin/loyaltyRewards/LoyaltyReward";
import ProductReferenceCombobox from "@widgets/featuredProducts/editor/card/productSearch/ProductReferenceCombobox";
import {api} from "@/data/api";
import {ProductReference} from "@/data/entities/product";

const setValues = (
    loyaltyReward: LoyaltyRewardDetails | null,
    setValue: UseFormSetValue<LoyaltyRewardFormValues>,
) => {
    if (!loyaltyReward) {
        return;
    }

    setValue("requiredPoints", loyaltyReward.requiredPoints);
    setValue("active", loyaltyReward.active);
    setValue("orderNumber", loyaltyReward.orderNumber);
    setValue("productId", loyaltyReward.product ? loyaltyReward.product.id : null);
};

type Props = SetLoyaltyRewardProps & {
    initialLoading: boolean,
    restaurantId: number,
};

const LoyaltyRewardDetailsForm = ({loyaltyReward, setLoyaltyReward, initialLoading, restaurantId}: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const {notify} = useNotifications();
    const {
        control,
        setValue,
        formState: {errors},
        register,
        handleSubmit,
        reset,
    } = useFormContext<LoyaltyRewardFormValues>();
    const {mutate: upsertFeaturedProduct} = useMutation({
        mutationFn: (form: LoyaltyRewardFormValues) => {
            if (loyaltyReward === null) {
                return api.loyaltyReward.createLoyaltyReward(restaurantId, form);
            } else {
                return api.loyaltyReward.updateLoyaltyReward(restaurantId, loyaltyReward.id, form);
            }
        },
        onSuccess: (upsertedLoyaltyReward: LoyaltyRewardDetails) => {
            if (loyaltyReward === null) {
                navigate(editLoyaltyReward(upsertedLoyaltyReward.id as number).absolutePath());
                window.scrollTo({top: 0});
            }

            setLoyaltyReward(upsertedLoyaltyReward);
            notifySuccess(intl.formatMessage({id: "saved"}), notify)
        },
        onError: () => notifyDanger(intl.formatMessage({id: "systemError"}), notify),
        onSettled: () => setLoading(false),
    });
    const disabledState = initialLoading || loading;
    const handleSubmitHandler: (() => void) = handleSubmit((data: LoyaltyRewardFormValues) => {
        setLoading(true);
        upsertFeaturedProduct(data);
    });

    // Set values after loading existing employee
    useEffect(() => {
        reset();
        setValues(loyaltyReward, setValue);
    }, [loyaltyReward, setValue, reset]);

    return (
        <div className={defaultFormGridClassname}>
            <div className={"md:col-span-2"}>
                <ToggleGroup
                    type={"checkbox"}
                    label={<InputLabel><FormattedMessage id={"status"}/></InputLabel>}
                    toggles={[
                        {
                            label: toggleDescriptionLabel(
                                <FormattedMessage id={"active"}/>,
                                <FormattedMessage id={"loyaltyRewardActiveExplanation"}/>,
                            ),
                            ...register("active"),
                        },
                    ]}
                    disabled={loading}
                    error={errors.active && errors.active.message}
                />
            </div>

            {
                loyaltyReward !== null &&
                <div
                    className={twJoin(
                        defaultFormGridClassname,
                        "md:col-span-2",
                    )}
                >
                    <TextField
                        type={"number"}
                        label={<InputLabel><FormattedMessage id={"id"}/></InputLabel>}
                        tip={<FormattedMessage id={"internalIdReferencePurposes"}/>}
                        defaultValue={loyaltyReward.id}
                        disabled={true}
                    />

                    <TextField
                        type={"text"}
                        label={<InputLabel><FormattedMessage id={"addedOn"}/></InputLabel>}
                        disabled={true}
                        defaultValue={formatDateTime(loyaltyReward.creationDate)}
                    />
                </div>
            }

            <div className={"md:col-span-2"}>
                <Controller
                    control={control}
                    name={"productId"}
                    rules={{
                        ...getInputValidationProps(loyaltyRewardFormValuesFormValidation.productId, intl),
                    }}
                    render={({field: {value, onChange}}) =>
                        <ProductReferenceCombobox
                            initialLoading={initialLoading}
                            restaurantId={restaurantId}
                            value={value}
                            onChange={(selected: ProductReference) => onChange(selected.id)}
                            error={errors.productId && errors.productId.message}
                        />
                    }
                />
            </div>

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"number"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"requiredPoints"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "requiredPointsExplanation"})}
                {...register("requiredPoints", {
                    ...getInputValidationProps(loyaltyRewardFormValuesFormValidation.requiredPoints, intl),
                })}
                disabled={disabledState}
                error={errors.requiredPoints && errors.requiredPoints.message}
            />
            <FormGridFiller/>

            <TextField
                containerClassname={animatePulse(initialLoading)}
                type={"number"}
                label={
                    <InputLabel hint={<FormattedMessage id={"required"}/>}>
                        <FormattedMessage id={"orderNumber"}/>
                    </InputLabel>
                }
                tip={intl.formatMessage({id: "loyaltyRewardOrderNumberTip"})}
                {...register("orderNumber", {
                    ...getInputValidationProps(loyaltyRewardFormValuesFormValidation.orderNumber, intl),
                })}
                disabled={disabledState}
                error={errors.orderNumber && errors.orderNumber.message}
            />
            <FormGridFiller/>

            <div className={"w-full md:col-span-2 flex flex-row items-center justify-end"}>
                <Button
                    props={{type: "submit"}}
                    iconBefore={<Icon icon={CheckIcon}/>}
                    buttonAction={{
                        as: "button",
                        actionProps: {
                            onClick: e => {
                                e.preventDefault();
                                handleSubmitHandler();
                            },
                        },
                    }}
                    disabled={disabledState}
                    loading={loading}
                >
                    <FormattedMessage id={"save"}/>
                </Button>
            </div>
        </div>
    );
};

export default LoyaltyRewardDetailsForm;